import React, { FC, useState, useEffect, useCallback, useRef, MouseEventHandler } from 'react';
import numeral from 'numeral';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { List, Set as ImmutableSet } from 'immutable';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useMount, usePrevious } from 'react-use';

import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MUISplitIcon from '@mui/icons-material/CallSplit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/DeleteOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { tagsTypes } from 'companion-app-components/flux/tags';
import { getLogger } from 'companion-app-components/utils/core';
import type RootState from 'companion-app-components/utils/redux-store/rootState';
import { chartOfAccountsTypes } from 'companion-app-components/flux/chart-of-accounts';
import { categoriesTypes, categoriesSelectors } from 'companion-app-components/flux/categories';
import { transactionsUtils, transactionsTypes } from 'companion-app-components/flux/transactions';

import useQCurrency from 'components/QCurrency/useQCurrency';
import QIconButton from 'components/QIconButton';
import DetailsPanel from 'components/DetailsPanel';
import TagsField from 'components/QuickenControls/TagsField/index';
import AmountField from 'components/QuickenControls/AmountField/index';
import CategoryField from 'components/QuickenControls/CategoryField/index';

import { getAccountString } from 'data/accounts/retrievers';
import { getMatchState, isExistingTxn } from 'data/transactions/utils';

import { copyObject, normalizeAmt } from 'utils/utils';

import { styles } from './styles';
import { getBaseAmount, sumSplitsRows, splitRowsRemainder, 
  countNonZeroRows, getNextField, clickPayeeLink, setInitialFocus } from './utils';

const useStyles = makeStyles()(styles as Record<string, any>);

const log = getLogger('component/SplitsAndDetailsPanel/index.tsx');

// ==============================================================
//
// Presents the form to edit/create splits for a transaction
//
// props:
//
// saveFn - the function to call with the object to save
// closeFn - the function to call to hide/dismiss the dialog
// txForm - the transaction record to edit
//
// Based on the value of the transaction coming in, the split amounts will base their "sign" on the
// transaction amount value.  If there is no value (0), then it will base it off the first category entered
// (or the category brought in from the new transaction).
//
// Once that is in place, amount signs will be either absolute, or * -1 (reverse of their actual value) for
// easy entry.
//
//

interface SplitsAndDetailsPanelProps {
  txn: transactionsTypes.CashFlowTransaction;
  closeFn: () => void;
  saveFn: (txn: Record<string, any>, autoSave?: boolean, txnDirty?: boolean, showSplitsState?: boolean) => void;
  deleteFn?: () => void;
  getSaveFn?: (save: boolean) => void;
  longCats?: boolean;
  showCurrencySymbol: boolean;
  onSizeChange?: () => void;
  showDetails: boolean;
  hideFooter?: boolean;
  unMatchAction?: () => void;
  goToTransferAction?: () => void;
  forceAutoSave?: boolean;
  editable?: boolean;
  splitSwitch: (splitSwitch: boolean) => void;
  showSplits: boolean;
  getTxnCurrency?: (txn: transactionsTypes.CashFlowTransaction) => string;
  onChangeTxnState?: (txn: transactionsTypes.CashFlowTransaction) => void;
  autoFocus?: boolean;
  onAmountChange?: (amount: number | string) => void;
}

const SplitsAndDetailsPanel: FC<SplitsAndDetailsPanelProps> = ({
  txn,
  closeFn,
  saveFn,
  deleteFn,
  getSaveFn,
  longCats,
  showCurrencySymbol,
  onSizeChange,
  showDetails,
  hideFooter,
  unMatchAction,
  goToTransferAction,
  forceAutoSave,
  editable = true,
  splitSwitch,
  showSplits,
  onChangeTxnState,
}) => {

  const { classes, theme } = useStyles();
  const { getTxnCurrency } = useQCurrency();

  const [txForm, setTxForm] = useState(txn.toJS());
  const [initialTxn, setInitialTxn] = useState(txn);
  const [isNewTxn, setIsNewTxn] = useState(!isExistingTxn(txn));
  const [baseAmount, setBaseAmount] = useState(getBaseAmount(txn));
  const [currency, setCurrency] = useState(getTxnCurrency(txn));
  const [txnDirty, setTxnDirty] = useState(false);
  const [rowsCalcModeOff, setRowsCalcModeOff] = useState(ImmutableSet([]));
  const [detailsForm, setDetailsForm] = useState(null);
  const [showSource, setShowSource] = useState(false);
  const [showSplitsState, setShowSplitsState] = useState(showSplits || (transactionsUtils.isSplitTxn(txn) && showDetails));
  const [wasSplit, setWasSplit] = useState(transactionsUtils.isSplitTxn(txn));
  const [autoFocusCategory, setAutoFocusCategory] = useState(true);
  const [isUpdating, setIsUpdating] = useState<null | boolean>(null);
  const [sizesChanged, setSizesChanged] = useState(true);

  const categoriesById = useSelector((state: RootState) => categoriesSelectors.getCategoriesById(state));

  const prevTxn = usePrevious(txn);
  const startingAmountValue = useRef<number | null>(null);
  const txFormRef = useRef(txForm);
  const detailsFormRef = useRef(detailsForm);
  const txnDirtyRef = useRef(txnDirty);
  // @ts-expect-error: Argument of type 'number' is not assignable to parameter of type 'string'.
  const manualZero = useRef(parseFloat(txn.amount) === 0);

  useEffect(() => {
    detailsFormRef.current = detailsForm;
  }, [detailsForm]);

  useEffect(() => {
    txnDirtyRef.current = txnDirty;
  }, [txnDirty]);

  useEffect(() => {
    txFormRef.current = txForm;
  }, [txForm]);

  useEffect(() => {
    if (txn !== txFormRef.current) {
      setTxForm(txn.toJS());
      setTxnDirty(true);
      if (showDetails) {
        setShowSplitsState((transactionsUtils.isSplitTxn(txn)));
      }
    }
  }, [txn, showDetails]);

  useEffect(() => {
    if (sizesChanged) {
      setSizesChanged(false);
      if (onSizeChange) {
        setTimeout(() => onSizeChange(), 50);
      }
    }
  }, [sizesChanged, onSizeChange]);

  const addSplitRow = useCallback((
    txObj, 
    coa: chartOfAccountsTypes.ChartOfAccountProps | null = null, 
    tags: tagsTypes.TagProps[] | List<tagsTypes.Tag> | null = null,
  ) => {
    if (!txObj.split || !txObj.split.items) {
      txObj.split = { // eslint-disable-line
        items: [],
      };
    }
    let itemAmount = 0;
    if (!manualZero.current) {
      itemAmount = baseAmount - (Number(sumSplitsRows(txObj.split.items)));
    } else {
      txObj.amount = sumSplitsRows(txObj.split.items); // eslint-disable-line
    }
    const newRow = {
      memo: '',
      tags,
      coa: coa || { type: 'UNCATEGORIZED', id: '0' },
      amount: numeral(itemAmount).format('0.00'),
    };
    txObj.split.items.push(newRow);
  }, [baseAmount]);

  // Use the first row as the uncategorized row while calculator mode is on for that row
  const splitUncategorizedRow = useCallback((txFormPassed, ignoreFirstRow = false) =>
    // @ts-expect-error: Argument of type '0' is not assignable to parameter of type 'never'.
    (isUpdating || ignoreFirstRow || rowsCalcModeOff.includes(0)) ?
      txFormPassed.split.items.findIndex((item) =>
        ((item.coa && item.coa.type === 'UNCATEGORIZED') && Number(item.coa.id) === 0)) : 0, [isUpdating, rowsCalcModeOff]);

  const initSplits = useCallback((addUncategorizedRow = true) => {
    let isUpdatingSplit: null | boolean = null;
    const hasSplits = transactionsUtils.isSplitTxn(txn);
    const updatedTxForm = copyObject(txForm);
    if (hasSplits) {
      isUpdatingSplit = true;
    } else {
      updatedTxForm.split = { items: [] };
      // if this is a new split,and there is a root category, make it the first row
      if (updatedTxForm.coa && (updatedTxForm.coa.type !== 'UNCATEGORIZED' && Number(updatedTxForm.coa.id) !== 0)) {
        addSplitRow(updatedTxForm, updatedTxForm.coa, updatedTxForm.tags);
      }
    }
    // if there is not an uncategorized row already, add one
    const splitItems = updatedTxForm.split?.items;
    if (splitItems &&
      splitItems.length <= 1 &&
      addUncategorizedRow &&
      (!hasSplits || splitUncategorizedRow(updatedTxForm) < 0)) {
      const txObj = updatedTxForm;
      addSplitRow(txObj, null, null);
      // if we didn't end up with at least 2 split rows, add one more
      if (txObj.split?.items?.length <= 1) {
        // Was causing new txn to have 2 Uncategorized line items
        addSplitRow(txObj, null, null);
      }
    }
    // finally, convert all amounts in split items to strings
    /* eslint-disable no-param-reassign */
    splitItems?.forEach((splitItem) => {
      splitItem.amount = numeral(splitItem.amount).format('0.00');
    });
    if (onChangeTxnState != null) {
      setTimeout(() => {
        setTxForm({ ...updatedTxForm });
        txFormRef.current = updatedTxForm;
      }, 100);
    } else {
      setTxForm({ ...updatedTxForm });
      txFormRef.current = updatedTxForm;
    }
    return isUpdatingSplit;
  }, [txn, txForm, addSplitRow, splitUncategorizedRow, onChangeTxnState]);

  const doAutoSaveRegister = () => forceAutoSave;

  const rebalanceRows = (txObj, index = -1) => {

    if (txObj.split && txObj.split.items && txObj.split.items.length > 0) {
      const unCategorizedRow = splitUncategorizedRow(txObj, index === 0);

      let newSizesChanged = index < 0;

      if (manualZero.current) {
        const amt = Number(sumSplitsRows(txObj.split.items));
        txObj.amount = normalizeAmt(amt);
        if (unCategorizedRow < 0) {
          addSplitRow(txObj);
          newSizesChanged = true;
        }
      } else {
        //
        // complicated rules here... if there is a remainder, we must allocate it
        // if there is an uncategorizedRow, and it is not the row we are on, then use it
        // otherwise create a new row
        //
        const remainder = splitRowsRemainder(txObj);

        if (remainder !== 0) {
          if (!wasSplit && index !== 0) {
            const item = txObj.split.items[0];
            item.amount = numeral(normalizeAmt(String(Number(item.amount) + remainder!))).format('0.00');
          } else if (unCategorizedRow >= 0) {
            if (unCategorizedRow !== index) {
              const item = txObj.split.items[unCategorizedRow];
              item.amount =
                numeral(normalizeAmt(String(Number(item.amount) + remainder!))).format('0.00');
            } else {
              const item = txObj.split.items[unCategorizedRow];
              item.amount =
                numeral(normalizeAmt(String(Number(item.amount) + remainder!))).format('0.00');
            }
            if (index === 0) {
              setWasSplit(true);
            }
          } else {
            addSplitRow(txObj);
            newSizesChanged = true;
          }
        }
        if (splitUncategorizedRow(txObj, true) < 0) {
          newSizesChanged = true;
        }
      }
      setTxForm({ ...txObj });
      txFormRef.current = txObj;
      setSizesChanged(newSizesChanged);
    }
  };

  const cleanUpRows = useCallback(() => {
    const validRows = countNonZeroRows(txFormRef.current);
    const updatedTxForm = { ...txForm };
    if (validRows === 1) {
      updatedTxForm.coa = updatedTxForm.split?.items?.[0]?.coa;
      // @ts-expect-error: 'updatedTxForm.split' is possibly 'null' or 'undefined'.
      updatedTxForm.split?.items = [];
    } else if (validRows !== 0) {
      updatedTxForm.coa = null;
    } else {
      updatedTxForm.split = { items: [] };
    }
    setTxForm({ ...updatedTxForm });
    txFormRef.current = updatedTxForm;
    return updatedTxForm;
  }, [txForm, setTxForm]);

  const saveFnMethod = (autoSave = true, close = true) => {
    // fire off a saveCategory action
    let txFormUpdated = txFormRef.current;
    const isSplit = transactionsUtils.isSplitTxn(txFormUpdated);
    log.log('SAVING SPLITs 1', isSplit, copyObject(txFormUpdated));
    if (isSplit) {
      if (manualZero.current) {
        // ensure the transaction amount is equal to the sum of the split rows
        txFormUpdated.amount = sumSplitsRows(txFormUpdated.split?.items);
      } else {
        txFormUpdated.amount = baseAmount;
      }

      txFormUpdated.coa = null;

      rebalanceRows(txFormUpdated);
      // if there is only 1 split item, don't use it.
      if (autoSave || close || countNonZeroRows(txFormUpdated) > 1) {
        txFormUpdated.coa = cleanUpRows().coa;
      }
      // remove any bogus tags
      txFormUpdated?.split?.items?.forEach((splitItem) => {
        if (splitItem.tags) {
          splitItem.tags = splitItem.tags.filter((tag) => tag.id !== '0');
        }
      });
    }

    // Merge in details if provided
    if (showDetails && detailsFormRef.current) {
      const { memo, check, tags, attachments, isBill } = detailsFormRef.current;

      const txObj = {
        ...txFormUpdated,
        memo,
        check,
        tags,
        attachments,
        isBill,
      };

      setTxForm((prev) => ({
        ...prev,
        memo,
        check,
        tags,
        attachments,
        isBill,
      }));
      
      txFormUpdated = txObj;
    }

    saveFn(txFormUpdated, autoSave, txnDirtyRef.current, !showSplitsState); // autosave

    if (close) {
      closeFn();
    }

    return true;
  };

  useMount(() => {

    setIsUpdating(showSplitsState ? initSplits() : null);
    setInitialFocus(true, log, showSplitsState);

    if (getSaveFn != null) {
      // @ts-expect-error: Argument of type '(autoSave?: boolean, close?: boolean) => boolean' is not assignable to parameter of type 'boolean'.
      getSaveFn(saveFnMethod);
    }
  });

  const closeAndExit = () => {
    if (!doAutoSaveRegister()) {
      saveFn(initialTxn.toJS(), false, true);
    }
    closeFn();
  };

  //------------------------------------------------------
  // handleKeyDown
  //
  //
  // Items that are relevant
  //
  // details-first-tab
  // details-last-tab
  // [name="row-0-field-category"]  (first tabbable field in split rows)
  // [name="row-${row}-field-amount"] (amount field for any row to skip the drop down button from category
  // splitLastTab (the last tabbable field in the split rows)
  // first-button
  // last-button
  //
  // And of course the feature flag splitsSideBySide determines the orientation of the splits and the details,
  // which impacts the tab order
  //

  const handleKeyDown = (e) => {
    // Hotkey source
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (doAutoSaveRegister()) {
        setTimeout(() => saveFnMethod(true, true));
      } else {
        closeAndExit();
      }
    }
    if (e.key === 'Enter') {
      // Save
      if (!doAutoSaveRegister()) {
        saveFnMethod();
      }
      e.stopPropagation();
    }
    if (e.key === 'Tab') {
      e.stopPropagation();
      // won't successfully pass the name of the TAGS field.  back/forward tab from Tags is default behavior
      const focusField = getNextField({ 
        splitsSideBySide: true, 
        splitShowNotes: true, 
        showDetails, 
        showSplitsState, 
      }, e.target.name, e.shiftKey ? 'backward' : 'forward');

      if (focusField) {
        e.preventDefault();
        let fld: Element | null | undefined | void = document.querySelectorAll(`[name="${focusField}"]`)[0];

        // This is a hack until we refactor the tags field.  Select will not pass down the 'name' property
        // to the input field (despite it being in inputProps), and thus, we cannot attach the name directly
        // to the input. So... we attach it to the wrapper, and have to 'dig in' to get the input
        if (focusField.indexOf('tags') > 0) {
          fld = fld.querySelector('button')?.focus && fld.querySelector('button')?.focus();
        }
        if (fld) {
          // @ts-expect-error: Property 'focus' does not exist on type 'Element'.
          fld.focus();
        }
      }
    }
  };

  const detailsChanged = (details, forceSave) => {
    setDetailsForm(details);
    setTxnDirty(true);
    if (doAutoSaveRegister() || forceSave) {
      setTimeout(() => saveFnMethod(doAutoSaveRegister() ? false : forceSave, false), 500);
    }
  };

  const clearSplits = (updatedTxn?: any) => {
    const newTxn = updatedTxn ?? txForm;
    if (transactionsUtils.isSplitTxn(newTxn)) {
      newTxn.coa = copyObject(newTxn.split?.items?.[0]?.coa || initialTxn.coa);
      newTxn.tags = copyObject(newTxn.split?.items?.[0]?.tags || null);
      newTxn.split = null;
      setShowSplitsState(false);
      setTxnDirty(true);
      setTxForm(newTxn);
      txFormRef.current = newTxn;
      saveFnMethod(false, !showDetails);
    }
    if (onSizeChange) {
      onSizeChange();
    }
  };

  useEffect(() => {
    if (prevTxn) {
      if (prevTxn.id !== txn.id) {
        setTxForm(txn.toJS());
        setInitialTxn(txn);
        setIsNewTxn(!isExistingTxn(txn));
        setBaseAmount(getBaseAmount(txn));
        setCurrency(getTxnCurrency(txn));
        setTxnDirty(false);
        setRowsCalcModeOff(ImmutableSet([]));
        setDetailsForm(null);
        setShowSource(false);
        setShowSplitsState(showSplits);
        setIsUpdating(showSplitsState ? initSplits() : null);
      }
      if ((prevTxn.split !== txn.split) && txn.split === null) {
        clearSplits(txn.toJS());
      }
    }
  // eslint-disable-next-line
  }, [txn, getTxnCurrency, initSplits, prevTxn, showSplits, showSplitsState]); 

  const showSplitsToggle = () => {
    const newState = !showSplitsState;
    if (!newState) {
      clearSplits();
      setShowSplitsState(false);
      // clearSplits
    } else {
      // initSplits
      initSplits(true);
      setShowSplitsState(true);
      splitSwitch(newState);
      saveFn(txFormRef.current, false, true);
    }
  };

  const renderSplitHeader = () => {
    const catHead = 'Split this transaction'; // isSplitTxn(txForm) ? 'Split' : 'Splits';
    const splitToggleClass = showDetails ? classes.pointer : null;
    return (
      <Paper elevation={0} className={classes.headerRow}>
        {/* -- CATEGORY FIELD -- */}
        <Paper elevation={0} className={classNames(classes.pointer, classes.splitTitleIcon)}>
          <MUISplitIcon />
        </Paper>
        <div className={classNames('flex2', classes.splitField, splitToggleClass)}>
          <Typography
            className={classes.splitTitle}
          >
            {catHead}
          </Typography>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {showSplitsState &&
              <>
                <Typography
                  mr="10"
                >
                  Transaction Total:
                </Typography>
                <Typography
                  mr="30"
                >
                  {`${numeral(baseAmount).format('0,00.00')}`}
                </Typography>
              </>}

            <Switch
              id={`split-toggle-${showSplitsState ? 'active' : 'inactive'}`}
              onClick={() => showSplitsToggle()}
              checked={showSplitsState}
              focusRipple
              inputProps={{
                name: 'splits-panel-onoff',
              }}
            />
          </div>

        </div>

      </Paper>
    );
  };

  const doAutoSave = () => {
    if (doAutoSaveRegister()) {
      saveFnMethod(false, false);
    }
  };

  //-------------------------------------------------------
  // handleChange
  //
  // This is where we update state of current edit field based on changes
  // global now with specific checks, TODO: make this more configuration driven, not if statements
  //
  const handleChange = (event, index, field) => {
    const txObj = copyObject(txForm);
    switch (field) {
      case 'category':
        if (!event) return;
        txObj.split.items[index].coa = event;
        setTxForm({ ...txObj });
        setTxnDirty(true);
        txFormRef.current = txObj;
        doAutoSave();
        break;
      case 'amount':
        txObj.split.items[index][field] = event.target.value;
        break;
      default:
        txObj.split.items[index][field] = event.target.value;
        onChangeTxnState?.(txObj);
        break;
    }
    setTxForm({ ...txObj });
    setTxnDirty(true);
    txFormRef.current = txObj;
  };

  const toggleCategoryFocus = (focusPassed = true) => {
    setAutoFocusCategory(focusPassed);
  };

  /*
    * RENDER CATEGORY FIELD
    */
  const renderCatField = (opts) => {
    const catFieldName = (opts.index === 0 && !showDetails) ? 'row-0-field-category' : null;
    return (
      // eslint-disable-next-line
      <div
        key={`split-cat-${opts.index}`}
        className={classNames('flex2', classes.textField)}
        onKeyDown={handleKeyDown}
      >
        <CategoryField
          InputProps={{ input: classes.ellipsePlaceholder }}
          name={`row-${opts.index}-field-category`}
          id={`row-${opts.index}-field-category`}
          onChange={(coa) => handleChange(coa, opts.index, 'category')}
          value={opts.item.coa}
          // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
          fontSize={theme.components?.register.fontSize.default}
          editable={editable}
          createEnabled
          recommendedCategoryType={opts.item.amount > 0 ? categoriesTypes.CategoryTypeEnum.INCOME : categoriesTypes.CategoryTypeEnum.EXPENSE}
          disableUnderline
          onBlur={() => toggleCategoryFocus(false)}
          initialFocus={autoFocusCategory && catFieldName !== null}
          longCats={longCats ?? false}
          onKeyDown={handleKeyDown}
          txn={txn}
        />
      </div>
    );
  };

  const tagsChange = (tags, index) => {
    const txObj = copyObject(txForm);
    txObj.split.items[index].tags = tags;
    setTxnDirty(true);
    setTxForm(txObj);
    setTimeout(doAutoSave, 1500);
  };

  /*
    * RENDER TAGS FIELD
  */
  const renderTagsField = (opts) => {

    const immutableTags = List(opts.item.tags || []);

    return (
      <div
        key={`split-tags-${opts.index}`}
        className={classNames('flex2', classes.textField)}
      >
        <TagsField
          onKeyDown={handleKeyDown}
          value={immutableTags}
          onChange={(tags) => tagsChange(tags, opts.index)}
          disableUnderline
          name={`row-${opts.index}-field-tags`}
          id={`row-${opts.index}-field-tags`}
          fieldId={`row-${opts.index}`}
          editable={editable}
        >
        </TagsField>
      </div>
    );
  };

  /*
    * RENDER NOTES FIELD
  */
  const renderNotesField = (opts) => (
    <div
      key={`split-notes-${opts.index}`}
      className={classNames(classes.textField)}
    >
      <TextField
        id={`row-${opts.index}-field-notes`}
        name={`row-${opts.index}-field-notes`}
        className={classes.inputStyle}
        onChange={(e) => handleChange(e, opts.index, 'memo')}
        onKeyDown={handleKeyDown}
        placeholder="Memo"
        value={opts.item.memo || ''}
        InputProps={{ disableUnderline: true }}
      />
    </div>
  );

  const handleAmountFocus = (event, index) => {
    startingAmountValue.current = Number(txForm.split?.items?.[index].amount);
  };

  const handleAmountBlur = (event, index) => {

    const txObj = txForm;
    if (txObj.split?.items?.[index]) {
      txObj.split.items[index].amount = event.target.value;
      rebalanceRows(txObj, index);

      if (Number(txObj.split.items[index].amount) !== startingAmountValue.current) {
        // @ts-expect-error: Argument of type 'any' is not assignable to parameter of type 'never'
        setRowsCalcModeOff(rowsCalcModeOff.add(index));
        doAutoSave();
      }
    }
  };

  /*
    * RENDER AMOUNT FIELD
  */
  const renderAmountField = (opts) => {

    const catObj = opts.item.coa && categoriesById.get(opts.item.coa.id);
    const isIncomeCategory = catObj && catObj.type === 'INCOME';

    return (
      <div
        key={`split-amount-${opts.index}`}
        className={classNames('amountField', 'flex2', classes.textField)}
      >
        <AmountField
          className={classes.inputStyle}
          name={`row-${opts.index}-field-amount`}
          id={`row-${opts.index}-field-amount`}
          onChange={(e) => handleChange(e, opts.index, 'amount')}
          onBlur={(e) => handleAmountBlur(e, opts.index)}
          onFocus={(e) => handleAmountFocus(e, opts.index)}
          onKeyDown={handleKeyDown}
          proposedSign={isIncomeCategory ? 1 : -1}
          // @ts-expect-error: Type 'string' is not assignable to type 'number'.
          value={String(opts.item.amount) || ''}
          editable
          disableUnderline
          amountType="amount"
          currencySymbol={showCurrencySymbol ? currency : 'N/A'}
        />
      </div>
    );
  };
  
  const renderOneSplitRow = (item, index, total) => {
    const lastRow = (index === (total - 1));
    const fields: React.JSX.Element[] = [];
    fields.push(renderCatField({ item, index }));

    fields.push(renderTagsField({ item, index }));
    fields.push(renderNotesField({
      item,
      index,
    }));
    fields.push(renderAmountField({ item, index }));

    const removeSplitRow = (row) => {
      const txObj = copyObject(txForm);
  
      txObj.split.items[row] = { memo: '', tags: null, coa: { type: 'UNCATEGORIZED', id: '0' }, amount: '0.00' };
      txObj.split.items.splice(row, 1);
  
      rebalanceRows(txObj);
      setTxnDirty(true);
      setTimeout(doAutoSave, 10);
    };

    const addOneSplitRow = () => {
      const txObj = copyObject(txForm);
      addSplitRow(txObj);
      setTxForm(txObj);
      if (onSizeChange) {
        onSizeChange();
      }
    };

    return (
      <Paper key={`spl-${index}`} elevation={0} className={classes.listRow}>
        {fields}
        {/* -- ADD ROW -- */}
        <Paper elevation={0} className={classNames(classes.pointer, classes.rowIcons)}>
          <QIconButton
            aria-label="Remove Split Row"
            onClick={() => removeSplitRow(index)}
            name={`row-${index}-remove-row`}
            id={`row-${index}-remove-row`}
            size="small"
          >
            <RemoveCircleIcon>
            </RemoveCircleIcon>
          </QIconButton>
          {lastRow &&
            <QIconButton
              className={classes.addIconButton}
              aria-label="Add Split Row"
              onClick={() => addOneSplitRow()}
              size="medium-no-padding"
              name={lastRow ? 'splits-last-tab' : null}
              id={`add-split-row-${index}`}
            >
              <AddCircleIcon />
            </QIconButton>}
        </Paper>
      </Paper>
    );
  };

  const renderSplitRows = () => {
    const splitRows: React.JSX.Element[] = [];
    // render the existing rows
    if (txForm.split) {
      txForm.split?.items?.forEach((item, index) => {
        // @ts-expect-error: Property 'length' does not exist
        splitRows.push(renderOneSplitRow(item, index, txForm.split?.items?.length));
      });
    }
    return splitRows;
  };

  /*
    * RENDER 'APPEARS ON...'
    * TEXT FIELD
  */
  const renderAppearsOn = () => {
    const { cpData } = txForm;
    let bankName;
    if (cpData) {
      bankName = getAccountString(txForm.accountId);
    }

    const matchVerb = 'match';

    return (
      txForm.cpData &&
      <div className={classes.detailsRow}>
        <div className={classes.rowIcon}>
          <AccountBalanceIcon />
        </div>
        <div className={classes.bankInfoContainer}>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
          >
            {`Appears on your ${bankName} statement as `}
            <Link
              onClick={() => clickPayeeLink(cpData?.payee)}
            >
              {cpData?.payee}
            </Link>
            {` on ${DateTime.fromFormat(cpData?.postedOn || '', 'yyyy-M-d').toLocaleString(DateTime.DATE_MED)} `}
            {showSource && `source is ${txForm.source}`}
          </Typography>

          {getMatchState(txForm) === 'matched' &&
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
            >
              This transaction was matched to an existing transaction when it was downloaded.&nbsp;
              {unMatchAction && transactionsUtils.canTxnBeUmatched(txn) &&
              <Link onClick={unMatchAction}>
                {`Un${matchVerb}`}
              </Link>}
            </Typography>}
        </div>
      </div>
    );
  };

  const renderSplitPanel = () => {
    const containerClass = showDetails ? 'sidebyside' : null;
    return (
      <div className={classNames(classes.panelContainer, !editable ? 'disable' : '')}>
        <Paper
          className={classNames(classes.panel)}
          onKeyDown={handleKeyDown}
          elevation={0}
        >
          <div className={classes.splitsDetailsParent}>
            {showDetails &&
              <Paper elevation={0} className={classNames(classes.webFirstDetails, containerClass)}>
                <DetailsPanel
                  onChange={detailsChanged}
                  txForm={txn}
                  onSizeChange={onSizeChange}
                  unMatchAction={unMatchAction}
                  firstTabName="details-first-tab"
                  lastTabName="details-last-tab"
                  goToTransferAction={goToTransferAction}
                  editable={editable}
                />
              </Paper>}
            <div className={showDetails ? classes.rightCol : classes.splitsOnly}>
              <Paper elevation={0} className={classNames(classes.splitsContainer, containerClass)}>
                {renderSplitHeader()}
                {showSplitsState && renderSplitRows()}
                {/* Section below is the 'appears on your statement as...' section
                  * This function is a bit of misnomer because the splits panel also renders the details panel
                  * so in this sense it's more like a parent flex container
                */}
              </Paper>
              {showDetails &&
                <>
                  {renderAppearsOn()}
                </>}

            </div>
          </div>
          {!hideFooter &&
            <div className={classes.buttonHolder}>
              {showDetails && !isNewTxn &&
                <Paper elevation={0} className={classes.buttonsLeft}>
                  <Button
                    className={classes.deleteButton}
                    onClick={deleteFn}
                    color="primary"
                    name="first-button"
                    id="splits-panel-delete"
                  >
                    Delete Transaction
                  </Button>
                </Paper>}
              <Paper elevation={0} className={classes.buttonsRight}>
                {!doAutoSaveRegister() &&
                  <Button
                    className={classes.buttons}
                    onClick={closeAndExit}
                    color="primary"
                    id="splits-panel-cancel"
                  >
                    Cancel
                  </Button>}
                <Button
                  className={classes.buttons}
                  onClick={(doAutoSaveRegister() ? closeAndExit : saveFnMethod) as MouseEventHandler<HTMLElement>}
                  name="last-button"
                  color="primary"
                  id="splits-panel-save"
                >
                  {doAutoSaveRegister() ? 'Close' : 'Save'}
                </Button>
              </Paper>
            </div>}
        </Paper>
      </div>
    );
  };

  return renderSplitPanel();
};

(SplitsAndDetailsPanel as any).whyDidYouRender = true;

export default SplitsAndDetailsPanel;
